import { useCallback, useEffect, useState } from "react";
import { PlusCircle, ShieldAlert, Lock, CheckCircle2, Info } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Iter 60 Block 3 ,  Add to Pipeline button + critical-stage + cap badges.
 *
 * Renders one of these states based on /api/pipeline-v2/{oid}/my-status:
 *   1. Not-yet-added, no critical elsewhere, cap ok      → active button.
 *   2. Not-yet-added, critical elsewhere                 → disabled + generic
 *      badge ("Advanced stage: another subscriber has this under LOI").
 *   3. Not-yet-added, free-tier cap reached              → disabled + upgrade
 *      prompt tooltip (distinct from critical, never conflated).
 *   4. Already in pipeline, critical elsewhere from OTHER user → keeps their
 *      row + passive badge.
 *   5. Already in pipeline, no critical elsewhere        → simple "In your
 *      pipeline · {stage}" chip.
 */
export default function AddToPipelineButton({ oid, onAdded }) {
  const [status, setStatus] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    if (!oid) return;
    try {
      const r = await api.get(`/pipeline-v2/${oid}/my-status`);
      setStatus(r.data);
    } catch {
      setStatus({ in_my_pipeline: false, aggregate: { critical_elsewhere: false }, cap: {} });
    }
  }, [oid]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    setAdding(true);
    try {
      await api.post(`/pipeline-v2/${oid}/add`, {});
      toast.success("Added to your pipeline");
      if (onAdded) onAdded();
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const code = d?.error;
      if (code === "monthly_limit_reached") {
        toast.error(d.message || "Free-tier monthly cap reached. Upgrade to Pro.");
      } else if (code === "critical_stage_elsewhere") {
        toast.error(d.aggregate_label || "Another subscriber has reached a critical stage.");
      } else if (code === "already_in_pipeline") {
        toast.info("Already in your pipeline");
      } else {
        toast.error(d?.message || (typeof d === "string" ? d : "Add failed"));
      }
      load();
    } finally {
      setAdding(false);
    }
  };

  if (!status) return null;

  const {
    in_my_pipeline: inMine,
    my_stage: myStage,
    aggregate = {},
    cap = {},
  } = status;
  const criticalElsewhere = !!aggregate.critical_elsewhere;
  const aggLabel = aggregate.aggregate_label;
  const capReached = !!cap.cap_reached;
  const capUsed = cap.used;
  const capLimit = cap.limit;

  // State 4/5 ,  already in user's pipeline
  if (inMine) {
    return (
      <div className="flex flex-wrap items-center gap-2" data-testid="pipeline-status-chip">
        <span className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider px-2 py-1 rounded-sm border border-emerald-500/40 bg-emerald-500/10 text-emerald-300">
          <CheckCircle2 className="h-3 w-3" /> In your pipeline · {myStage}
        </span>
        {criticalElsewhere && aggLabel && (
          <span
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-sm border border-amber-500/40 bg-amber-500/10 text-amber-300"
            title="Another subscriber reached a critical stage on this opportunity. Your existing entry is unaffected ,  this is informational only."
            data-testid="critical-elsewhere-badge"
          >
            <ShieldAlert className="h-3 w-3" /> {aggLabel}
          </span>
        )}
      </div>
    );
  }

  // State 2 ,  critical elsewhere, block new adds
  if (criticalElsewhere) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <button
          disabled
          data-testid="add-to-pipeline-btn-critical-disabled"
          className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-sm border border-slate-700 bg-slate-800/50 text-slate-500 cursor-not-allowed"
          title={aggLabel || "Another subscriber reached a critical stage on this opportunity. New pipeline adds are locked until they mark it dead or closed."}
        >
          <Lock className="h-3.5 w-3.5" /> Add to Pipeline
        </button>
        <span
          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-sm border border-amber-500/40 bg-amber-500/10 text-amber-300"
          data-testid="critical-elsewhere-badge"
        >
          <ShieldAlert className="h-3 w-3" /> {aggLabel}
        </span>
      </div>
    );
  }

  // State 3 ,  free-tier cap reached
  if (capReached) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <button
          disabled
          data-testid="add-to-pipeline-btn-cap-disabled"
          className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-sm border border-slate-700 bg-slate-800/50 text-slate-500 cursor-not-allowed"
          title={`Free plan includes ${capLimit ?? 5} opportunities per month (${capUsed ?? "?"}/${capLimit ?? 5} used). Upgrade to Pro to add more.`}
        >
          <Lock className="h-3.5 w-3.5" /> Add to Pipeline
        </button>
        <a
          href="/app/billing?plan=pro"
          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-sm border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20"
          data-testid="cap-upgrade-cta"
        >
          <Info className="h-3 w-3" /> {capUsed ?? 5}/{capLimit ?? 5} used · Upgrade to Pro
        </a>
      </div>
    );
  }

  // State 1 ,  active button
  return (
    <button
      onClick={add}
      disabled={adding}
      data-testid="add-to-pipeline-btn"
      className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-sm border border-emerald-500/50 bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25 disabled:opacity-50"
    >
      <PlusCircle className="h-3.5 w-3.5" /> {adding ? "Adding…" : "Add to Pipeline"}
    </button>
  );
}
